import { useQueryClient } from '@tanstack/react-query';
import { type SelfDescribingJson } from '@ev-tech/snowplow-library';

import { getShopContextCommonValues } from '@/utils/snowplowContext';
import { getShopFromQueryCache } from '@/utils/getShopFromQueryCache';
import { useDidomi } from '@/providers/DidomiContextProvider';
import { useGTMTracking } from '@/hooks/usGTMTracking';
import { useTrackPageView } from '@/hooks/useTrackPageView';
import { useGAEvent, useGTMTackingIdsEvents } from '@/hooks/useGAEvent';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useExposeGAEventOnce } from '@/hooks/useExposeGAEventOnce';
import { useListing } from '@/hooks/queries/useListing';

type ExposeTrackingEffectsProps = {
  contexts: {
    pageContext: SelfDescribingJson;
    shopContext: SelfDescribingJson[];
    propertyContext: SelfDescribingJson[];
    agentContext: SelfDescribingJson[];
    cookieConsentContext: SelfDescribingJson;
    locationContext: SelfDescribingJson[];
  };
};

export const ExposeTrackingEffects = ({ contexts }: ExposeTrackingEffectsProps) => {
  const { listingId, hasLicencePartnerGAAccount } = useExposePageProps();
  const { isReady: isDidomiReady } = useDidomi();
  const queryClient = useQueryClient();
  const {
    data: { listing, genericListing },
  } = useListing();
  const shop = getShopFromQueryCache({ queryClient, listing });

  const isPageViewReady = Boolean(genericListing) && isDidomiReady;

  useTrackPageView(
    [
      contexts.pageContext,
      ...contexts.agentContext,
      ...contexts.shopContext,
      ...contexts.propertyContext,
      ...contexts.locationContext,
      contexts.cookieConsentContext,
    ],
    {
      isReady: isPageViewReady,
      trackingKey: listingId,
    },
  );
  useGAEvent('searchDetailViewLicensePartner', hasLicencePartnerGAAccount);
  useGTMTackingIdsEvents({ listing });
  useGTMTracking(listing ? { masterdataShopId: getShopContextCommonValues({ listing, shop }).shop_id } : {});
  useExposeGAEventOnce();

  return null;
};
